import React from "react";
import { color, radius, space, text, weight, motion, shadow } from "../tokens";

/**
 * <Button> — the whole button ergonomics you need.
 *
 *   variant  : "primary"   — brand solid (ONE per view — Von Restorff)
 *              "secondary" — border only, subtle bg on hover
 *              "ghost"     — text only, bg on hover
 *              "danger"    — danger solid (for destructive actions)
 *   size     : "sm" (32) | "md" (36) default | "lg" (40)
 *   loading  : boolean — replaces icon with spinner, label stays
 *   iconLeft, iconRight : ReactNode
 *   fullWidth : boolean
 *
 * Icon-left convention. 8px gap. Focus ring 4px offset on :focus-visible.
 */
const SIZE_MAP = {
  sm: { h: 32, fs: text.base, px: space[3] },
  md: { h: 36, fs: text.md,   px: space[4] },
  lg: { h: 40, fs: text.md,   px: space[5] },
};

function stylesForVariant(variant) {
  switch (variant) {
    case "primary":
      return {
        base: { background: color.brand.solid, color: "#FFF", border: `1px solid ${color.brand.solid}` },
        hover: { background: color.brand.hover, borderColor: color.brand.hover },
        active: { background: color.brand.active, borderColor: color.brand.active },
      };
    case "secondary":
      return {
        base: { background: color.bg.surface, color: color.text.primary, border: `1px solid ${color.border.strong}` },
        hover: { background: color.bg.subtle },
        active: { background: color.bg.hover },
      };
    case "ghost":
      return {
        base: { background: "transparent", color: color.text.secondary, border: `1px solid transparent` },
        hover: { background: color.bg.subtle, color: color.text.primary },
        active: { background: color.bg.hover },
      };
    case "danger":
      return {
        base: { background: color.danger.solid, color: "#FFF", border: `1px solid ${color.danger.solid}` },
        hover: { background: "#902420", borderColor: "#902420" },
        active: { background: "#7A1E1A", borderColor: "#7A1E1A" },
      };
    default:
      return stylesForVariant("secondary");
  }
}

export function Button({
  children,
  variant = "secondary",
  size = "md",
  loading = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  disabled = false,
  type = "button",
  onClick,
  style,
  ...rest
}) {
  const [hover, setHover] = React.useState(false);
  const [active, setActive] = React.useState(false);
  const s = SIZE_MAP[size];
  const v = stylesForVariant(variant);
  const isDisabled = disabled || loading;

  const merged = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: space[2],
    height: s.h,
    padding: `0 ${s.px}px`,
    borderRadius: radius.md,
    fontFamily: "inherit",
    fontSize: s.fs,
    fontWeight: weight.semibold,
    lineHeight: 1,
    cursor: isDisabled ? "not-allowed" : "pointer",
    opacity: isDisabled ? 0.55 : 1,
    transition: motion.tx("all", "fast"),
    width: fullWidth ? "100%" : undefined,
    ...v.base,
    ...(hover && !isDisabled ? v.hover : {}),
    ...(active && !isDisabled ? v.active : {}),
    ...style,
  };

  return (
    <button
      type={type}
      disabled={isDisabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      onFocus={(e) => { e.currentTarget.style.boxShadow = variant === "danger" ? shadow.focusDanger : shadow.focus; }}
      onBlur={(e) => { e.currentTarget.style.boxShadow = "none"; }}
      onClick={onClick}
      style={merged}
      {...rest}
    >
      {loading ? <Spinner size={size === "sm" ? 12 : 14} /> : iconLeft}
      {children && <span>{children}</span>}
      {iconRight && !loading && iconRight}
    </button>
  );
}

function Spinner({ size = 14 }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        border: `2px solid currentColor`,
        borderRightColor: "transparent",
        borderRadius: "50%",
        display: "inline-block",
        animation: "idz-spin 0.65s linear infinite",
      }}
    />
  );
}
